import { useEffect, useRef, useState } from 'react';
import api from '@/utils/api';

/**
 * useBusinessMemoryPrefill
 * ------------------------
 * Loads the user's saved business memory when a Money Bot modal opens and
 * calls the provided setters IF they are currently empty. The user's own
 * input always wins — we never overwrite something they've typed.
 *
 * Usage:
 *   useBusinessMemoryPrefill(isOpen, {
 *     businessDescription: [businessDescription, setBusinessDescription],
 *     targetCustomer:      [targetCustomer, setTargetCustomer],
 *     offer:               [offer, setOffer],
 *     price:               [price, setPrice],
 *   });
 *
 * Accepted field keys (each maps to a memory field):
 *   businessDescription → business_description || core_solution || primary_offer
 *   targetCustomer / targetAudience → target_audience
 *   offer / offerName / productName → primary_offer
 *   price / pricePoint → price_point
 *   niche → niche
 *
 * Returns { memory, loaded } so callers can show a "Using your saved business
 * memory" banner or react to the loaded state.
 */
const EMPTY_RE = /^\s*$/;

function isEmpty(value) {
  return value == null || (typeof value === 'string' && EMPTY_RE.test(value));
}

const FIELD_TO_MEMORY = {
  businessDescription: ['business_description', 'core_solution', 'primary_offer', 'business_name'],
  targetCustomer: ['target_audience'],
  targetAudience: ['target_audience'],
  targetReader: ['target_audience'],
  offer: ['primary_offer', 'offer_name'],
  offerName: ['primary_offer', 'offer_name'],
  productName: ['primary_offer', 'offer_name'],
  price: ['price_point', 'price'],
  pricePoint: ['price_point', 'price'],
  niche: ['niche'],
  tone: ['tone_of_voice'],
  businessName: ['business_name'],
  painPoint: ['core_problem'],
  // Ebook Builder: topic falls back to primary offer or core solution so the
  // ebook is about the business, not generic filler
  topic: ['primary_offer', 'core_solution', 'niche'],
};

export default function useBusinessMemoryPrefill(isOpen, fields = {}) {
  const [memory, setMemory] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const appliedOnceRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      appliedOnceRef.current = false;
      return;
    }
    if (appliedOnceRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/business-memory/profile');
        const mem = res.data?.memory || (res.data?.has_memory === false ? null : res.data) || null;
        if (cancelled) return;
        setMemory(mem);
        if (mem) {
          // Round 108.1 — race-safe prefill. The caller may have
          // populated a field between the initial render and when
          // this async fetch resolved (e.g. a mission-handoff
          // effect setting businessDescription BEFORE the memory
          // GET responds). Using a functional setter lets us read
          // the LATEST state at commit time and skip fields the
          // user (or mission handoff) has already filled. The
          // captured `currentValue` was stale by construction.
          Object.entries(fields).forEach(([fieldKey, tuple]) => {
            if (!Array.isArray(tuple) || tuple.length < 2) return;
            // eslint-disable-next-line no-unused-vars
            const [_currentValue, setter] = tuple;
            const memKeys = FIELD_TO_MEMORY[fieldKey] || [fieldKey];
            let candidate = null;
            for (const mk of memKeys) {
              const v = mem[mk];
              if (v && String(v).trim()) {
                candidate = String(v);
                break;
              }
            }
            if (candidate == null) return;
            setter((prev) => (isEmpty(prev) ? candidate : prev));
          });
        }
      } catch {
        // No memory / network failure — just leave the form as-is
        if (!cancelled) setMemory(null);
      } finally {
        if (!cancelled) {
          setLoaded(true);
          appliedOnceRef.current = true;
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return { memory, loaded };
}
